import { z } from 'zod'
import { AI_MODELS, CHECK_INTERVALS, TIERS } from '../types/enums.ts'

const PromptItemSchema = z.object({ id: z.string(), promptText: z.string() })

/**
 * Schema for messages pushed to the MENTION_QUEUE.
 * Every consumer must validate against this before processing.
 *
 * Accepts either `prompts` (new) or `keywords` (legacy) to avoid dropping
 * in-flight messages at deploy time. Both are normalised to `prompts`.
 */
export const QueueJobMessageSchema = z.union([
  z.object({
    jobId: z.string(),
    brandId: z.string(),
    userId: z.string(),
    brandName: z.string(),
    industry: z.string(),
    prompts: z.array(PromptItemSchema).min(1),
    models: z.array(z.enum(AI_MODELS)).min(1),
    tier: z.enum(TIERS).optional(),
    interval: z.enum(CHECK_INTERVALS),
    brandUrl: z.string().nullable().optional(),
  }),
  z.object({
    jobId: z.string(),
    brandId: z.string(),
    userId: z.string(),
    brandName: z.string(),
    industry: z.string(),
    keywords: z.array(z.object({ id: z.string(), keyword: z.string() })).min(1),
    models: z.array(z.enum(AI_MODELS)).min(1),
    tier: z.enum(TIERS).optional(),
    interval: z.enum(CHECK_INTERVALS),
    promptTemplate: z.string().nullable().optional(),
    brandUrl: z.string().nullable().optional(),
  }).transform((msg) => ({
    jobId: msg.jobId,
    brandId: msg.brandId,
    userId: msg.userId,
    brandName: msg.brandName,
    industry: msg.industry,
    prompts: msg.keywords.map((k) => ({ id: k.id, promptText: k.keyword })),
    models: msg.models,
    tier: msg.tier,
    interval: msg.interval,
    brandUrl: msg.brandUrl,
  })),
])

export type QueueJobMessage = {
  jobId: string
  brandId: string
  userId: string
  brandName: string
  industry: string
  prompts: { id: string; promptText: string }[]
  models: (typeof AI_MODELS)[number][]
  tier?: (typeof TIERS)[number]
  interval: (typeof CHECK_INTERVALS)[number]
  brandUrl?: string | null
}
